import { Initiative, NUMBER_OF_HOMES, NumberOfHomes } from '@schema/initiative'
import { PhaseType } from '@schema/phase'
import {
  InitiativeCreationStep,
  initiativeCreationSteps,
} from '../blocks/workflow'
import { CreationGuide } from '../blocks/CreationGuide'
import { useEffect, useState } from 'react'
import { useInitiativeAutoSave } from '../blocks/useInitiativeAutoSave'
import Column from '@app/components/layout/Column'
import { useTranslations } from '@i18n/useLanguage'
import { LocalitySelector } from '@app/components/blocks/smartInputs/LocalitySelector'
import { SearchSelectOption } from '@app/components/core/input/searchSelect/SearchSelect'
import { RegionSelector } from '@app/components/blocks/smartInputs/RegionSelector'
import { Select } from '@app/components/core/input/select/Select'
import { YesNoToggle } from '@app/components/core/toggle/YesNoToggle'
import { Text } from '@app/components/core/typography/Text'
import { Style } from 'inlines'
import { TextInput } from '@app/components/core/input/text/TextInput'
import { MultiLineTextInput } from '@app/components/core/input/text/MultiLineTextInput'

export const MembersParametersStep = ({
  initiative,
  selectedPhaseType,
  completeStep,
  uncompleteStep,
}: {
  initiative: Partial<Initiative>
  selectedPhaseType: PhaseType
  completeStep: (step: InitiativeCreationStep) => void
  uncompleteStep: (step: InitiativeCreationStep) => void
}) => {
  const translations = useTranslations().app
  const creationTranslations = translations.dashboard.initiative.creation
  const [searchingForMembers, setSearchingForMembers] = useState(
    initiative.searchingForMembers,
  )
  const [searchingForMembersDescription, setSearchingForMembersDescription] =
    useState(initiative.searchingForMembersDescription ?? '')
  const [numberOfHomes, setNumberOfHomes] = useState<NumberOfHomes>(
    initiative.numberOfHomes,
  )
  const [projectSize, setProjectSize] = useState(initiative.projectSize)
  const [region, setRegion] = useState<SearchSelectOption>({
    key: initiative.location.address.region?.id,
    value: initiative.location.address.region?.name ?? '',
  })
  const [locality, setLocality] = useState<SearchSelectOption>({
    key: initiative.location.address.locality?.id,
    value: initiative.location.address.locality?.name ?? '',
  })
  // const [purchaseBudget, setPurchaseBudget] =
  //   useState<PurchaseBudgetPreference>(initiative.buyBudget)
  // const [rentalBudget, setRentalBudget] = useState<RentalBudgetPreference>(
  //   initiative.rentalBudget,
  // )

  useInitiativeAutoSave(initiative.id, {
    searchingForMembers,
    searchingForMembersDescription,
    numberOfHomes,
    projectSize,
    location: {
      address: {
        locality: locality.key ? { id: locality.key } : undefined,
        region: region.key ? { id: region.key } : undefined,
        country: undefined,
        municipality: undefined,
        street: null,
        postalCode: null,
      },
      geo: undefined,
    },
    // buyBudget: purchaseBudget,
    // rentalBudget,
  })

  useEffect(() => {
    if (region.key) {
      completeStep(initiativeCreationSteps.membersParameters)
    } else {
      uncompleteStep(initiativeCreationSteps.membersParameters)
    }
  }, [region, completeStep, uncompleteStep])

  // const rentalBudgetOptions = Object.values(RENTAL_BUDGET_PREFERENCE).map(
  //   (x) => ({
  //     key: x,
  //     value: creationTranslations.rentalBudgetOptions[x],
  //     selected: x === rentalBudget,
  //   }),
  // )

  const emptyLabel = translations.common.notSpecified

  return (
    <CreationGuide
      selectedPhaseType={selectedPhaseType}
      currentStep={initiativeCreationSteps.membersParameters}
    >
      <Column gap="2.5rem" alignItems="stetch">
        <Column gap="0.5rem">
          <Text.Medium isBold={true}>
            {creationTranslations.areYouLookingForMembers}
          </Text.Medium>
          <YesNoToggle
            yesSelected={searchingForMembers}
            setYesSelected={setSearchingForMembers}
          />
        </Column>
        {searchingForMembers && (
          <Column gap="0.38rem" alignItems="stretch">
            <Column gap="0.25rem" alignItems="stretch">
              <Text.Medium style={labelStyle}>
                {creationTranslations.lookingForMembersTitle}
              </Text.Medium>
              <Text.Medium color="var(--palette-blue-500)" isBold={true}>
                {creationTranslations.lookingForMembersSubtitle}
              </Text.Medium>
            </Column>
            <MultiLineTextInput
              placeholder={creationTranslations.lookingForMembersPlaceholder}
              value={searchingForMembersDescription}
              setValue={setSearchingForMembersDescription}
            />
          </Column>
        )}
        <Select
          label={creationTranslations.numberOfHomes}
          labelStyle={labelStyle}
          value={numberOfHomes}
          setValue={setNumberOfHomes}
          notSelectedLabel={emptyLabel}
          options={Object.values(NUMBER_OF_HOMES).map((item) => ({
            value: item,
            label:
              creationTranslations.numberOfHomesOptions[item] ?? emptyLabel,
          }))}
        />
        <TextInput
          label={creationTranslations.projectSize}
          labelStyle={labelStyle}
          text={projectSize}
          setText={setProjectSize}
        />
        <RegionSelector
          region={region}
          setRegion={setRegion}
          labelStyle={labelStyle}
          displayLabelAsterisk={true}
        />
        <LocalitySelector
          locality={locality}
          setLocality={setLocality}
          filterByRegionId={region.key ?? undefined}
          labelStyle={labelStyle}
        />
        {/* <Select
          label={creationTranslations.purchaseBudget}
          labelStyle={labelStyle}
          value={purchaseBudget}
          setValue={setPurchaseBudget}
          notSelectedLabel={emptyLabel}
          options={Object.values(PURCHASE_BUDGET_PREFERENCE).map((item) => ({
            value: item,
            label:
              creationTranslations.purchaseBudgetOptions[item] ?? emptyLabel,
          }))}
        /> */}
        {/* <TagSelection<RentalBudgetPreference>
          label={creationTranslations.rentalBudget}
          subTitle={creationTranslations.selectOne}
          tags={rentalBudgetOptions}
          onSelect={setRentalBudget}
        /> */}
      </Column>
    </CreationGuide>
  )
}

const labelStyle: Style = {
  color: 'var(--brand-secondary)',
  fontWeight: 500,
}
